import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';

export interface LocationForecastMapping {
  forecast_department_id: string | null;
  forecast_department_name: string | null;
  /** override — привязано к локации; organization — унаследовано от организации */
  source: 'override' | 'organization' | null;
}

export interface LocationDepartment {
  id: string;
  name: string;
  code_1c: string | null;
  organization_id: string | null;
}

// Точка продаж (aqniet/iiko) локации
export const useLocationForecastMapping = (locationId: string | undefined) => {
  return useQuery({
    queryKey: ['locations', locationId, 'forecast-mapping'],
    queryFn: async (): Promise<LocationForecastMapping> => {
      return (await apiClient.get(
        `/api/v1/locations/${locationId}/forecast-mapping`,
      )) as LocationForecastMapping;
    },
    enabled: !!locationId,
  });
};

export const useSetLocationForecastMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      locationId,
      forecast_department_id,
      forecast_department_name,
    }: {
      locationId: string;
      forecast_department_id: string | null;
      forecast_department_name?: string | null;
    }) => {
      return (await apiClient.put(
        `/api/v1/locations/${locationId}/forecast-mapping`,
        { forecast_department_id, forecast_department_name },
      )) as LocationForecastMapping;
    },
    onSuccess: (_, { locationId }) => {
      queryClient.invalidateQueries({
        queryKey: ['locations', locationId, 'forecast-mapping'],
      });
    },
  });
};

// Подразделения (1С), относящиеся к точке
export const useLocationDepartments = (locationId: string | undefined) => {
  return useQuery({
    queryKey: ['locations', locationId, 'departments'],
    queryFn: async (): Promise<LocationDepartment[]> => {
      return (await apiClient.get(
        `/api/v1/locations/${locationId}/departments`,
      )) as LocationDepartment[];
    },
    enabled: !!locationId,
  });
};

export const useSetLocationDepartments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      locationId,
      department_ids,
    }: {
      locationId: string;
      department_ids: string[];
    }) => {
      return (await apiClient.put(
        `/api/v1/locations/${locationId}/departments`,
        { department_ids },
      )) as LocationDepartment[];
    },
    onSuccess: (_, { locationId }) => {
      queryClient.invalidateQueries({
        queryKey: ['locations', locationId, 'departments'],
      });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
};
